import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  directionOf,
  formatPrice,
  formatYears,
  translate,
  yearsNoun,
  type Locale,
  type MessageKey,
} from './i18n.ts';

/**
 * Locale plumbing.
 *
 * `dir` is set on `<html>` rather than on a wrapper so the browser's own
 * bidirectional algorithm, text selection and form controls all agree with the
 * layout. A `dir` on a div and `lang` on another is how competitor sites end up
 * with mirrored punctuation.
 */

interface LocaleValue {
  locale: Locale;
  dir: 'rtl' | 'ltr';
  t(key: MessageKey): string;
  years(count: number): string;
  yearsUnit(count: number): string;
  price(sar: number): string;
  setLocale(locale: Locale): void;
}

const LocaleContext = createContext<LocaleValue | null>(null);

export function LocaleProvider({
  locale,
  setLocale,
  children,
}: {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  children: ReactNode;
}) {
  const value = useMemo<LocaleValue>(
    () => ({
      locale,
      dir: directionOf(locale),
      t: (key) => translate(locale, key),
      years: (count) => formatYears(locale, count),
      yearsUnit: (count) => yearsNoun(locale, count),
      price: (sar) => formatPrice(locale, sar),
      setLocale,
    }),
    [locale, setLocale],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale called outside LocaleProvider');
  return value;
}
